import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, ApiError, getToken, setToken, type PublicUser, type RegisterInput } from '../lib/api.ts';
import { clearLocalData } from '../lib/db.ts';

const USER_KEY = 'klarnus.user';

const readCachedUser = (): PublicUser | null => {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as PublicUser) : null;
  } catch {
    return null;
  }
};

const writeCachedUser = (user: PublicUser | null) => {
  try {
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_KEY);
  } catch {
    // Storage blocked; the session still works for as long as the tab is open.
  }
};

export interface AuthValue {
  user: PublicUser | null;
  status: 'loading' | 'authenticated' | 'anonymous';
  login(email: string, password: string): Promise<void>;
  register(input: RegisterInput): Promise<void>;
  verify(token: string): Promise<void>;
  logout(): Promise<void>;
  setUser(user: PublicUser): void;
  /** Starts a guest account, the way in until sign-in exists. Safe to call repeatedly. */
  startGuest(): Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  // A stored token means the user is in, immediately, with the profile last seen. Waiting for
  // /api/auth/me would put the network in front of the card list — exactly what this app is
  // supposed to avoid.
  const [user, setUserState] = useState<PublicUser | null>(() => (getToken() ? readCachedUser() : null));
  const [status, setStatus] = useState<AuthValue['status']>(() => (getToken() ? 'authenticated' : 'anonymous'));

  const applySession = useCallback((token: string, nextUser: PublicUser) => {
    setToken(token);
    writeCachedUser(nextUser);
    setUserState(nextUser);
    setStatus('authenticated');
  }, []);

  const signOut = useCallback(async () => {
    setToken(null);
    writeCachedUser(null);
    setUserState(null);
    setStatus('anonymous');
    // The next account to log in on this device must not inherit these cards.
    await clearLocalData().catch(() => {});
  }, []);

  useEffect(() => {
    if (status !== 'authenticated') return;
    let cancelled = false;
    void (async () => {
      try {
        const { user: fresh } = await api.me();
        if (cancelled) return;
        writeCachedUser(fresh);
        setUserState(fresh);
      } catch (err) {
        // Only a real rejection logs the user out. Being offline must not.
        if (!cancelled && err instanceof ApiError && err.status === 401) await signOut();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [status, signOut]);

  // One guest per device at a time, however many screens ask for it.
  const guestRequest = useRef<Promise<void> | null>(null);
  const startGuest = useCallback(() => {
    guestRequest.current ??= api.guest()
      .then((session) => applySession(session.token, session.user))
      .finally(() => { guestRequest.current = null; });
    return guestRequest.current;
  }, [applySession]);

  const value = useMemo<AuthValue>(
    () => ({
      user,
      status,
      async login(email, password) {
        const session = await api.login(email, password);
        applySession(session.token, session.user);
      },
      async register(input) {
        await api.register(input);
      },
      async verify(token) {
        const session = await api.verify(token);
        applySession(session.token, session.user);
      },
      async logout() {
        await api.logout().catch(() => {});
        await signOut();
      },
      setUser(next) {
        writeCachedUser(next);
        setUserState(next);
      },
      startGuest,
    }),
    [user, status, applySession, signOut, startGuest],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
